import React, { useState } from 'react';
import { CommentInput, CommentDisplay, CommentList } from './';
import { commentService } from '../services/comments';

/**
 * CommentDemo - Demo component for testing comment system
 * Shows all comment features and Russian text support
 */
export const CommentDemo: React.FC = () => {
  const [setComment, setSetComment] = useState('');
  const [exerciseComment, setExerciseComment] = useState('');
  const [workoutComment, setWorkoutComment] = useState('');
  
  const [savedComments, setSavedComments] = useState<Array<{
    id: string;
    comment: string;
    timestamp: string;
    type: 'set' | 'workout' | 'exercise';
  }>>([]);

  const addComment = (comment: string, type: 'set' | 'workout' | 'exercise') => {
    if (comment.trim()) {
      const newComment = {
        id: `comment-${Date.now()}`,
        comment: comment.trim(),
        timestamp: new Date().toISOString(),
        type
      };
      setSavedComments([newComment, ...savedComments]);
      
      // Clear the input
      if (type === 'set') setSetComment('');
      if (type === 'exercise') setExerciseComment('');
      if (type === 'workout') setWorkoutComment('');
    }
  };

  const stats = commentService.getCommentStats(savedComments.map(c => c.comment));

  return (
    <div className="comment-demo p-4 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Система комментариев</h1>
        <p className="text-gray-600">
          Демонстрация системы комментариев с поддержкой русского языка и быстрых фраз
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-6">
          <h2 className="text-lg font-semibold">Ввод комментариев</h2>
          
          {/* Set Comment */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">Комментарий к подходу</h3>
            <CommentInput
              value={setComment}
              onChange={setSetComment}
              type="set"
              placeholder="Как прошёл подход?"
              context={{
                exerciseType: 'Жим лёжа',
                weight: 80,
                reps: 10,
                previousComments: ['Легко', 'В запасе']
              }}
            />
            <button
              onClick={() => addComment(setComment, 'set')}
              disabled={!setComment.trim()}
              className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Добавить комментарий к подходу
            </button>
          </div>

          {/* Exercise Comment */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">Комментарий к упражнению</h3>
            <CommentInput
              value={exerciseComment}
              onChange={setExerciseComment}
              type="exercise"
              placeholder="Как выполнялось упражнение?"
              context={{
                exerciseType: 'Подтягивания',
                previousComments: ['Хорошая техника', 'Полная амплитуда']
              }}
            />
            <button
              onClick={() => addComment(exerciseComment, 'exercise')}
              disabled={!exerciseComment.trim()}
              className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Добавить комментарий к упражнению
            </button>
          </div>

          {/* Workout Comment */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">Комментарий к тренировке</h3>
            <CommentInput
              value={workoutComment}
              onChange={setWorkoutComment}
              type="workout"
              placeholder="Как прошла тренировка?"
              context={{
                workoutDuration: 65,
                previousComments: ['Отличная тренировка!', 'Много энергии']
              }}
            />
            <button
              onClick={() => addComment(workoutComment, 'workout')}
              disabled={!workoutComment.trim()}
              className="mt-3 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Добавить комментарий к тренировке
            </button>
          </div>

          {/* Quick Phrases Demo */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-3">Быстрые фразы</h3>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Для подходов:</h4>
                <div className="flex flex-wrap gap-2">
                  {commentService.getSetQuickPhrases().slice(0, 6).map(phrase => (
                    <button
                      key={phrase}
                      onClick={() => setSetComment(phrase)}
                      className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded hover:bg-blue-200"
                    >
                      {phrase}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Для тренировок:</h4>
                <div className="flex flex-wrap gap-2">
                  {commentService.getWorkoutQuickPhrases().slice(0, 4).map(phrase => (
                    <button
                      key={phrase}
                      onClick={() => setWorkoutComment(phrase)}
                      className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded hover:bg-purple-200"
                    >
                      {phrase}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Display Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Сохранённые комментарии</h2>
            {savedComments.length > 0 && (
              <button
                onClick={() => setSavedComments([])}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Очистить все
              </button>
            )}
          </div>

          {/* Statistics */}
          {savedComments.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium mb-3">Статистика</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Всего:</span>
                  <span className="ml-2 font-medium">{stats.total}</span>
                </div>
                <div>
                  <span className="text-gray-600">Средняя длина:</span>
                  <span className="ml-2 font-medium">{stats.averageLength} символов</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Русский текст:</span>
                  <span className={`ml-2 font-medium ${stats.hasRussianText ? 'text-green-600' : 'text-gray-400'}`}>
                    {stats.hasRussianText ? 'Да' : 'Нет'}
                  </span>
                </div>
              </div>
              
              {stats.mostCommon.length > 0 && (
                <div className="mt-3">
                  <span className="text-gray-600 text-sm">Популярные:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {stats.mostCommon.slice(0, 3).map(comment => (
                      <span key={comment} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {comment}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Comments List */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <CommentList
              comments={savedComments}
              showTimestamps={true}
              compact={false}
            />
          </div>
        </div>
      </div>

      {/* Features Info */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Возможности системы комментариев:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Поддержка русского языка и кириллицы</li>
          <li>• Быстрые фразы для подходов и тренировок</li>
          <li>• Контекстные предложения на основе упражнения и данных</li>
          <li>• Валидация и форматирование текста</li>
          <li>• Статистика использования комментариев</li>
          <li>• Поиск по быстрым фразам</li>
        </ul>
      </div>
    </div>
  );
};

export default CommentDemo;